import Vue from 'vue'
import Vuex from 'vuex'

import parseAuthToken from '@/mixins/JWT.js'

Vue.use(Vuex)
Vue.config.devtools = true

import { RepositoryFactory } from '@/data/RepositoryFactory.js'
import { authModule } from "@/auth/store";
const PCMRepository = RepositoryFactory.get('pcm')
const OpointRepository = RepositoryFactory.get('opoint')

// Function for checking if PCM API response is successful
const isSuccessful = function(response) {
    return response.status === 200 && response.headers['content-type'].indexOf('application/json') > -1 && 'data' in response.data
}

// Checks if a authorization failed, and if so, logs out.
const authorizationFailed = function(response) {
    if (response.status === 401) {
        return true
    }
    return false
}

export default new Vuex.Store({
    modules: {
        auth: authModule,
    },
    state: {
        logins: [],
        site: null,
        company: null,
        logEvents: [],
        pagination: {
            totalItems: 0,
            perPage: 100,
            page: 1
        },
        articles: [],
        logs: [],
        articleslogsPage: 1,
        sort: null,
        sortDir: 'desc',
        filters: {
            status: -1,
            search: null,
            logEvents: [],
            date: {
                min: null,
                max: null
            },
            successfulAttempts: false,
            failedAttempts: false
        },
        loading: false,
        error: false,
        cache: null
    },
    mutations: {
        updateSearch(state, n) {
            state.filters.search = n
        },
        updateStatus(state, n) {
            state.filters.status = n
        },
        updateLogEventsFilter(state, n) {
            state.filters.logEvents = n
        },
        updateLogEvents(state, n) {
            state.logEvents = n
        },
        updateDateMin(state, n) {
            state.filters.date.min = n
        },
        updateDateMax(state, n) {
            state.filters.date.max = n
        },
        updateSort(state, n) {
            state.sort = n
        },
        updateSortDir(state, n) {
            state.sortDir = n
        },
        updateLoading(state, n) {
            state.loading = n
        },
        updateError(state, n) {
            state.error = n
        },
        updateSite(state, n) {
            state.site = n
        },
        updateCompany(state, n) {
            state.company = n
        },
        updateSuccessfulAttempts(state, n) {
            state.filters.successfulAttempts = n
        },
        updateFailedAttempts(state, n) {
            state.filters.failedAttempts = n
        },
        updateArticles(state, payload) {
            if (payload.page == state.articleslogsPage) {
                state.articles = payload.articles
            } else {
                state.articles = state.articles.concat(payload.articles)
            }
        },
        updateLogs(state, payload) {
            if (payload.page == state.articleslogsPage) {
                state.logs = payload.logs
            } else {
                state.logs = state.logs.concat(payload.logs)
            }
        },
        updateArticlesLogsPage(state, n) {
            state.articleslogsPage = n
        },
        updateCache(state, payload) {
            state.cache = payload
        },
        updateAccounts(state, n) {
            // If we simply update the state directly, vue will not detect any changes
            Vue.set(state, 'accounts', n)
        },
    },
    actions: {
        init({ dispatch }) {
            return dispatch('auth/init')
        },
        fetchCache({ commit }) {
            return new Promise((resolve, reject) => {
                PCMRepository.getCache(localStorage.getItem('token'))
                .then(response => {
                    if (response.data && response.data.data) {
                        commit('updateCache', response.data.data)
                        resolve(response)
                    }
                }).catch(error => {
                    reject(error)
                })
            })
        },
        fetchLogs({ commit }, payload) {
            return new Promise((resolve, reject) => {
                PCMRepository.getLogs(localStorage.getItem('token'), payload.id_site, payload.page)
                .then(response => {
                    if ('data' in response.data && 'logs' in response.data['data']) {
                        commit('updateArticles', [])
                        commit('updateLogs', {logs: response.data['data']['logs'], page: payload.page})
                        resolve(response)
                    } else {
                        reject(response)
                    }
                }).catch(error => {
                    reject(error)
                })
            })
        },
        fetchArticles({ commit }, payload) {
            return new Promise((resolve, reject) => {
                PCMRepository.getArticles(localStorage.getItem('token'), payload.id_site, payload.page)
                .then(response => {
                    if ('data' in response.data && 'articles' in response.data['data']) {
                        commit('updateLogs', [])
                        commit('updateArticles', {articles: response.data['data']['articles'], page: payload.page})
                        resolve(response)
                    } else {
                        reject(response)
                    }
                }).catch(error => {
                    reject(error)
                })
            })
        },
        fetchSite({ commit }, id_site) {
            commit('updateLoading', true)
            PCMRepository.getFromSite(localStorage.getItem('token'), id_site).then((response) => {
                if (isSuccessful(response)) {
                    commit('updateError', false)
                    commit('updateSite', response.data['data']['site'])
                    commit('updateAccounts', response.data['data']['accounts'])
                } else {
                    commit('updateError', true)
                    if (authorizationFailed(response)) {
                        commit('auth/logOut')
                    }
                }
                commit('updateLoading', false)
            })
        },
        fetchCompany({ commit }, id_customer) {
            commit('updateLoading', true)
            PCMRepository.getFromCompany(localStorage.getItem('token'), id_customer).then((response) => {
                if (isSuccessful(response)) {
                    commit('updateError', false)
                    commit('updateCompany', response.data['data']['company'])
                    commit('updateAccounts', response.data['data']['accounts'])
                } else {
                    commit('updateError', true)
                    if (authorizationFailed(response)) {
                        commit('auth/logOut')
                    }
                }
                commit('updateLoading', false)
            })
        },
        fetchLogins({ commit, state }) {
            commit('updateLoading', true)
            PCMRepository.getAll(localStorage.getItem('token'), state.pagination.page, state.sort, state.sortDir, state.filters)
            .then((response) => {
                if (isSuccessful(response)) {
                    commit('updateError', false)

                    state.logins = response.data['data']['data']
                    state.pagination.totalItems = response.data['data']['count']

                    let totalPages = Math.max(1, Math.ceil(state.pagination.totalItems / state.pagination.perPage))
                    if (totalPages < state.pagination.page) {
                        state.pagination.page = totalPages
                    }

                    this.dispatch('fetchCache')
                } else {
                    commit('updateError', true)
                }
            }).then(() => {
                commit('updateLoading', false)
            })
        },
        fetchLogEvents({ commit }) {
            PCMRepository.getLogEvents(localStorage.getItem('token'), ).then((response) => {
                if (isSuccessful(response)) {
                    commit('updateLogEvents', response.data['data'])
                } else if (authorizationFailed(response)) {
                    commit('auth/logOut')
                }
            })
        }
    }
})
